/**
 * Facturation Tab
 * Main billing/invoicing tab with dynamic toggle switch:
 * - Default: Recherche | Produits | Devis | Fact. groupee
 * - After search: Retour | Factures | Notes de credit
 */

import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  CubeIcon,
  UserGroupIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ReceiptRefundIcon,
  ClipboardDocumentListIcon,
  ArrowUturnLeftIcon,
  Bars3Icon,
  ScaleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import RechercheView, { type RechercheSubTab } from './components/RechercheView/RechercheView';
import ProductsView from './components/ProductsView/ProductsView';
import DevisView from './components/DevisView/DevisView';
import { GroupFacturationView } from './components/GroupFacturationView';
import { RapprochementView } from './components/RapprochementView';
import { ParametresView } from './components/ParametresView/ParametresView';
import { AffaireContextSidebar } from './components/AffaireContextSidebar';
import './facturation.css';

// Main tab type
type MainTab = 'recherche' | 'products' | 'devis' | 'group-facturation' | 'rapprochement' | 'parametres';

export const FacturationTab: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Main navigation state
  const [activeMainTab, setActiveMainTab] = useState<MainTab>('recherche');

  // Search results state - when true, toggle shows Retour/Factures/Notes de credit
  const [isInSearchResults, setIsInSearchResults] = useState(false);

  // Sub-tab for search results
  const [searchSubTab, setSearchSubTab] = useState<RechercheSubTab>('factures');

  // Pre-selected affaire from navigation state (e.g., from Tracker page)
  const [preselectedAffaireId, setPreselectedAffaireId] = useState<number | null>(null);

  // Return path when coming from another page (e.g., Tracker)
  const [returnPath, setReturnPath] = useState<string | null>(null);

  // Return tab when coming from another tab within facturation (e.g., group-facturation)
  const [returnTab, setReturnTab] = useState<MainTab | null>(null);

  // Selected affaire ID (for showing Produits tab)
  const [selectedAffaireId, setSelectedAffaireId] = useState<string | null>(null);

  // Mobile sidebar visibility
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Ref to reset RechercheView
  const rechercheKey = useRef(0);

  // Handle pre-selected affaire from navigation state
  useEffect(() => {
    const state = location.state as {
      preselectedAffaireId?: number;
      preselectedContactId?: string;
      highlightAffaireId?: string;
      from?: string;
      fromTab?: MainTab;
      resetView?: boolean;
    } | null;

    // Handle resetView - when user clicks on Facturation tab while already on it
    if (state?.resetView) {
      setActiveMainTab('recherche');
      setIsInSearchResults(false);
      setSearchSubTab('factures');
      setPreselectedAffaireId(null);
      setSelectedAffaireId(null);
      setReturnPath(null);
      setReturnTab(null);
      rechercheKey.current += 1;
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
      return;
    }

    // Inbound from ProductsEmbeddedView "Voir la prochaine facturation
    // mensuelle" CTA: jump straight to Fact. groupée with the contact
    // preselected. We leave location.state intact so GroupFacturationView's
    // own useEffect can read preselectedContactId / highlightAffaireId and
    // clear the state itself.
    if (state?.preselectedContactId) {
      setActiveMainTab('group-facturation');
      if (state.from) setReturnPath(state.from);
      if (state.fromTab) setReturnTab(state.fromTab);
      return;
    }

    if (state?.preselectedAffaireId) {
      setPreselectedAffaireId(state.preselectedAffaireId);
      setActiveMainTab('recherche');
      // Store the return path if provided
      if (state.from) {
        setReturnPath(state.from);
      }
      // Store the return tab if provided (for internal navigation within facturation)
      if (state.fromTab) {
        setReturnTab(state.fromTab);
      }
      // Clear the state to prevent re-triggering on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Handle return to previous page or tab
  const handleReturnToSource = () => {
    // If we came from another tab within the same facturation page
    if (returnTab) {
      setActiveMainTab(returnTab);
      setIsInSearchResults(false);
      setReturnPath(null);
      setReturnTab(null);
      setPreselectedAffaireId(null);
      setSelectedAffaireId(null);
      rechercheKey.current += 1;
      return;
    }
    // Otherwise navigate to the return path
    if (returnPath) {
      navigate(returnPath);
    }
  };

  // Handle going back from search results
  const handleBackFromSearch = () => {
    setIsInSearchResults(false);
    setSearchSubTab('factures');
    setPreselectedAffaireId(null); // Clear pre-selected affaire when going back
    setSelectedAffaireId(null); // Clear selected affaire for Produits tab
    rechercheKey.current += 1; // Force RechercheView to remount and reset
  };

  // Clear pre-selected affaire after it's been used by RechercheView
  const handlePreselectedAffaireUsed = () => {
    setPreselectedAffaireId(null);
  };

  // Handle search state change from RechercheView
  const handleSearchStateChange = (inResults: boolean, affaireIdFromSearch?: string | null) => {
    setIsInSearchResults(inResults);
    if (inResults) {
      // Default to produits when affaire is selected, otherwise factures.
      // Use the value passed from RechercheView (selectedAffaireId state isn't
      // updated yet in this synchronous call due to closure staleness).
      const affaireId = affaireIdFromSearch !== undefined ? affaireIdFromSearch : selectedAffaireId;
      setSearchSubTab(affaireId ? 'produits' : 'factures');
    }
  };

  // Determine which toggle options to show
  const isSearchResultsMode = activeMainTab === 'recherche' && isInSearchResults;

  // Determine if we should show the return button (from another page or from group-facturation tab)
  const showReturnButton = returnPath || returnTab;

  // Calculate active index for toggle
  const getActiveIndex = () => {
    if (isSearchResultsMode) {
      // With return button + affaire: 0=Produits, 1=Factures, 2=Notes de credit
      // With return button no affaire: 0=Factures, 1=Notes de credit
      // Without return button + affaire: 0=Retour (not active), 1=Produits, 2=Factures, 3=Notes de credit
      // Without return button no affaire: 0=Retour (not active), 1=Factures, 2=Notes de credit
      if (showReturnButton) {
        if (selectedAffaireId) {
          if (searchSubTab === 'produits') return 0;
          if (searchSubTab === 'factures') return 1;
          return 2; // notes-credit
        }
        // No affaire - only Factures and Notes de credit
        if (searchSubTab === 'factures') return 0;
        return 1; // notes-credit
      }
      // Without return button
      if (selectedAffaireId) {
        if (searchSubTab === 'produits') return 1;
        if (searchSubTab === 'factures') return 2;
        return 3; // notes-credit
      }
      // No affaire - only Retour, Factures, Notes de credit
      if (searchSubTab === 'factures') return 1;
      return 2; // notes-credit
    } else {
      // Normal mode: 0=Recherche, 1=Produits, 2=Devis, 3=Fact. groupee, 4=Rapprochement, 5=Paramètres
      if (activeMainTab === 'recherche') return 0;
      if (activeMainTab === 'products') return 1;
      if (activeMainTab === 'devis') return 2;
      if (activeMainTab === 'group-facturation') return 3;
      if (activeMainTab === 'rapprochement') return 4;
      return 5;
    }
  };

  return (
    <div className={`fact-tab ${showReturnButton ? 'fact-tab--with-return' : ''}`}>
      {/* Hero Header Row (with optional return button) */}
      <div className="fact-hero-row">
        {/* Return Button (when coming from Tracker or another facturation tab) */}
        {showReturnButton && (
          <button
            onClick={handleReturnToSource}
            className="fact-return-btn"
          >
            <ArrowUturnLeftIcon className="w-4 h-4" />
            <span>Retour</span>
          </button>
        )}

        {/* Hero Header */}
        <div className="fact-hero">
          <div className="fact-hero-content">
            <h1>Facturation</h1>
            <p>Gestion des factures, notes de crédit et produits</p>
          </div>
          <div className="fact-hero-decoration">
            <div className="fact-hero-circle fact-hero-circle-1"></div>
            <div className="fact-hero-circle fact-hero-circle-2"></div>
            <div className="fact-hero-circle fact-hero-circle-3"></div>
          </div>
        </div>
      </div>

      {/* Dynamic Toggle Switch */}
      <div className="fact-toggle-wrapper">
        <div
          className="fact-toggle-switch"
          data-active-index={getActiveIndex()}
          data-item-count={
            isSearchResultsMode
              ? (showReturnButton
                  ? (selectedAffaireId ? "3" : "2")
                  : (selectedAffaireId ? "4" : "3"))
              : "6"
          }
        >
          {isSearchResultsMode ? (
            // Search results mode: Produits (first when affaire selected) | Factures | Notes de credit
            <>
              {!showReturnButton && (
                <button
                  onClick={handleBackFromSearch}
                  className="fact-toggle-option fact-toggle-option--back"
                >
                  <ArrowLeftIcon />
                  Retour
                </button>
              )}
              {/* Produits tab - first position when a specific affaire is selected */}
              {selectedAffaireId && (
                <button
                  onClick={() => setSearchSubTab('produits')}
                  className={`fact-toggle-option ${searchSubTab === 'produits' ? 'active' : ''}`}
                >
                  <CubeIcon />
                  Produits
                </button>
              )}
              <button
                onClick={() => setSearchSubTab('factures')}
                className={`fact-toggle-option ${searchSubTab === 'factures' ? 'active' : ''}`}
              >
                <DocumentTextIcon />
                Factures
              </button>
              <button
                onClick={() => setSearchSubTab('notes-credit')}
                className={`fact-toggle-option ${searchSubTab === 'notes-credit' ? 'active' : ''}`}
              >
                <ReceiptRefundIcon />
                Notes de credit
              </button>
            </>
          ) : (
            // Normal mode: Recherche | Produits | Devis | Statistiques
            <>
              <button
                onClick={() => setActiveMainTab('recherche')}
                className={`fact-toggle-option ${activeMainTab === 'recherche' ? 'active' : ''}`}
              >
                <MagnifyingGlassIcon />
                Recherche
              </button>
              <button
                onClick={() => setActiveMainTab('products')}
                className={`fact-toggle-option ${activeMainTab === 'products' ? 'active' : ''}`}
              >
                <CubeIcon />
                Produits
              </button>
              <button
                onClick={() => setActiveMainTab('devis')}
                className={`fact-toggle-option ${activeMainTab === 'devis' ? 'active' : ''}`}
              >
                <ClipboardDocumentListIcon />
                Devis
              </button>
              <button
                onClick={() => setActiveMainTab('group-facturation')}
                className={`fact-toggle-option ${activeMainTab === 'group-facturation' ? 'active' : ''}`}
              >
                <UserGroupIcon />
                Fact. groupee
              </button>
              <button
                onClick={() => setActiveMainTab('rapprochement')}
                className={`fact-toggle-option ${activeMainTab === 'rapprochement' ? 'active' : ''}`}
              >
                <ScaleIcon />
                Rapprochement
              </button>
              <button
                onClick={() => setActiveMainTab('parametres')}
                className={`fact-toggle-option ${activeMainTab === 'parametres' ? 'active' : ''}`}
              >
                <Cog6ToothIcon />
                Paramètres
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content with optional sidebar */}
      <div className={`fact-content-wrapper ${selectedAffaireId ? 'fact-content-wrapper--with-sidebar' : ''}`}>
        {/* Main content */}
        <div className="fact-content">
          {activeMainTab === 'recherche' && (
            <RechercheView
              key={rechercheKey.current}
              activeSubTab={searchSubTab}
              onSearchStateChange={handleSearchStateChange}
              preselectedAffaireId={preselectedAffaireId}
              onPreselectedAffaireUsed={handlePreselectedAffaireUsed}
              onAffaireSelected={setSelectedAffaireId}
            />
          )}
          {activeMainTab === 'products' && <ProductsView />}
          {activeMainTab === 'devis' && <DevisView />}
          {activeMainTab === 'group-facturation' && <GroupFacturationView />}
          {activeMainTab === 'rapprochement' && <RapprochementView />}
          {activeMainTab === 'parametres' && <ParametresView />}
        </div>

        {/* Context sidebar (visible when affaire is selected) */}
        {selectedAffaireId && (
          <>
            {/* Mobile toggle button */}
            <button
              className="fact-sidebar-toggle"
              onClick={() => setMobileSidebarOpen(true)}
              aria-label="Voir les details de l'affaire"
            >
              <Bars3Icon className="w-5 h-5" />
            </button>

            {/* Sidebar */}
            <AffaireContextSidebar
              affaireId={parseInt(selectedAffaireId, 10)}
              isOpen={mobileSidebarOpen}
              onClose={() => setMobileSidebarOpen(false)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default FacturationTab;
